import React, { useState } from "react";
import TableToolbar from "./TableToolbar";
import TableBody from "./TableBody";
import TablePagination from "./TablePagination";
import TableFilters from "./TableFilters";

const getValue = (obj, path) => {
  return path.split(".").reduce((current, key) => {
    return current?.[key];
  }, obj);
};

const filterBySearch = (row, query, headers) => {
  if (!query) return true;
  const visibleKeys = headers.filter((h) => h.show).map((h) => h.key);
  return visibleKeys.some((key) =>
    (getValue(row, key) ?? "")
      .toString()
      .toLowerCase()
      .includes(query.toLowerCase())
  );
};

// Filter function
const filterByAdvanced = (row, filters) => {
  return Object.entries(filters).every(([key, filterValue]) => {
    if (!filterValue || (Array.isArray(filterValue) && !filterValue.length)) {
      return true; // No filter applied
    }

    const value = getValue(row, key);

    if (Array.isArray(filterValue)) {
      const values = filterValue.map((v) =>
        typeof v === "object" ? v.value || v.label : v
      );
      return values.includes(value);
    }

    if (typeof filterValue === "object") {
      return value === filterValue.value || value === filterValue.label;
    }

    if (typeof value === "string" && typeof filterValue === "string") {
      return value.toLowerCase().includes(filterValue.toLowerCase());
    }

    return value === filterValue;
  });
};

const sortData = (data, { key, direction }) => {
  if (!key || !direction) return data;

  return [...data].sort((a, b) => {
    let va = getValue(a, key) ?? "";
    let vb = getValue(b, key) ?? "";

    const numA = +va;
    const numB = +vb;
    if (!isNaN(numA) && !isNaN(numB))
      return direction === "asc" ? numA - numB : numB - numA;

    const dateA = new Date(va);
    const dateB = new Date(vb);
    if (!isNaN(dateA) && !isNaN(dateB))
      return direction === "asc" ? dateA - dateB : dateB - dateA;

    return direction === "asc"
      ? va.toString().localeCompare(vb.toString(), undefined, { numeric: true })
      : vb
        .toString()
        .localeCompare(va.toString(), undefined, { numeric: true });
  });
};

const renderCell = (head, row, { onStatusChange }) => {
  const value = getValue(row, head.key);

  switch (head.type) {
    case "link":
      if (!head.hasId) return value ?? "-";
      return (
        <span
          className="text-primary text-decoration-none"
        >
          {value}
        </span>
      );

    case "date":
      return value ? new Date(value).toLocaleDateString() : "-";

    case "currency":
      return value != null ? `₹${Number(value).toLocaleString()}` : "-";

    case "badge":
      return <span className="badge bg-success">{value}</span>;

    case "select":
      return (
        <select
          className="form-select form-select-sm"
          value={value}
          onChange={(e) => onStatusChange?.(row.Id, e.target.value)}
        >
          {head.options?.map((opt) =>
            typeof opt === "string" ? (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ) : (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            )
          )}
        </select>
      );

    case "custom":
      return head.renderer?.(value, row);

    default:
      return value ?? "-";
  }
};

const paginate = (data, page, size) => {
  const start = (page - 1) * size;
  return data.slice(start, start + size);
};

const RecordDataTable = ({
  tableHeaders = [],
  tableData = [],
  permission = {},
  onDelete,
  onSelectRows,
  onStatusChange,
  handleRowClick,
  filtersConfig = [],
  handleEdit,
}) => {
  const [headers, setHeaders] = useState(tableHeaders);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort, setSort] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(
    filtersConfig.reduce(
      (acc, f) => ({ ...acc, [f.key]: f.type === "multiselect" ? [] : "" }),
      {}
    )
  );

  const safeTableData = Array.isArray(tableData) ? tableData : [];

  let rows = safeTableData.filter(
    (r) => filterBySearch(r, search, headers) && filterByAdvanced(r, filters)
  );
  rows = sortData(rows, sort);
  const currentRows = paginate(rows, page, rowsPerPage);

  const allSelected =
    currentRows.length &&
    currentRows.every((r) => selected.some((s) => s.Id === r.Id));

  const handleSelectAll = (e) => {
    const updated = e.target.checked ? currentRows : [];
    setSelected(updated);
    onSelectRows?.(updated);
  };

  const handleSelectRow = (row) => {
    const updated = selected.some((r) => r.Id === row.Id)
      ? selected.filter((r) => r.Id !== row.Id)
      : [...selected, row];
    setSelected(updated);
    onSelectRows?.(updated);
  };

  const toggleSort = (key, label) => {
    setSort((prev) => {
      if (prev?.key === key) {
        return {
          key,
          label,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, label, direction: "asc" };
    });
  };

  return (
    <div className="detail-h3 p-3 mb-4">
      <TableToolbar
        searchQuery={search}
        setSearchQuery={setSearch}
        tableData={safeTableData}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        headersState={headers}
        setHeadersState={setHeaders}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        isFiter={filtersConfig.length}
      />

      <div className="mb-2">
        {sort?.key ? (
          <>
            <strong>Sorted by:</strong>
            <span className="ms-2 badge bg-info">
              {sort.label} ({sort.direction === "asc" ? "A-Z" : "Z-A"})
              <i
                className="ms-2 cursor-pointer fas fa-times"
                title="Clear sorting"
                onClick={() => setSort({ key: null, direction: "asc" })}
              ></i>
            </span>
          </>
        ) : (
          <span className="ms-2 text-muted">No sorting applied</span>
        )}
      </div>

      {Object.values(filters).some(
        (value) =>
          value !== "" &&
          value !== null &&
          !(Array.isArray(value) && value.length === 0)
      ) && (
          <div className="mb-2">
            <strong>Filtered by:</strong>
            {Object.entries(filters).map(([key, value]) => {
              if (
                value === "" ||
                value === null ||
                (Array.isArray(value) && value.length === 0)
              )
                return null;

              const label =
                filtersConfig.find((f) => f.key === key)?.label || key;

              // Normalize display value
              let displayValue = "";
              if (Array.isArray(value)) {
                displayValue = value
                  .map((v) => (typeof v === "object" ? v.label || v.value : v))
                  .join(", ");
              } else if (typeof value === "object") {
                displayValue = value.label || value.value || "";
              } else {
                displayValue = value;
              }
              const handleRemoveFilter = () => {
                setFilters((prev) => {
                  const updated = { ...prev };
                  delete updated[key];
                  return updated;
                });
              };

              return (
                <span
                  key={key}
                  className="badge bg-secondary me-2 d-inline-flex align-items-center"
                  style={{ gap: "6px" }}
                >
                  {label}: {displayValue}
                  <i
                    className="fas fa-times ms-1 cursor-pointer"
                    onClick={handleRemoveFilter}
                    title="Remove filter"
                    style={{ fontSize: "0.8rem" }}
                  ></i>
                </span>
              );
            })}
          </div>
        )}

      {showFilters && (
        <TableFilters
          filtersConfig={filtersConfig}
          filters={filters}
          setFilters={setFilters}
        />
      )}

      <TableBody
        headersState={headers}
        sortConfig={sort}
        toggleSort={toggleSort}
        currentRows={currentRows}
        selectedRows={selected}
        handleRowSelect={handleSelectRow}
        areAllRowsSelected={allSelected}
        handleSelectAll={handleSelectAll}
        renderCell={renderCell}
        permission={permission}
        onDelete={onDelete}
        onStatusChange={onStatusChange}
        handleRowClick={handleRowClick}
        handleEdit={handleEdit}
      />

      <TablePagination
        currentPage={page}
        setCurrentPage={setPage}
        rowsPerPage={rowsPerPage}
        totalRows={rows.length}
      />
    </div>
  );
};

export default RecordDataTable;
